// Phase 5.2: stories voiced before word timings existed have no read-along. One alignment per
// language matches the existing audio to its text — nothing is re-voiced.
import type { AdminAlignMissing } from '@fonus/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AudioLines } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { api } from '../lib/api.ts';
import { humanFromApi } from '../lib/errors.ts';
import { newKey } from '../lib/utils.ts';
import { Confirm } from './common.tsx';
import { ErrorCard } from './ErrorCard.tsx';
import { Button } from './ui/button.tsx';

export function AlignMissing({ isOwner }: { isOwner: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(newKey);
  const q = useQuery({
    queryKey: ['align-missing'],
    queryFn: () => api<AdminAlignMissing>('/admin/align-missing'),
  });
  const run = useMutation({
    mutationFn: () =>
      api<{ jobs: number }>('/admin/align-missing', {
        method: 'POST',
        body: { idempotencyKey: key },
      }),
    onSuccess: (r) => {
      toast.success(`Запущена синхронизация: ${String(r.jobs)} — прогресс в «Журнале»`);
      setOpen(false);
      setKey(newKey());
      void qc.invalidateQueries({ queryKey: ['align-missing'] });
      void qc.invalidateQueries({ queryKey: ['readiness'] });
    },
  });
  if (!q.data || q.data.versions === 0) return null;
  const { versions, minutes } = q.data;
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3 rounded-[16px] border border-line bg-card p-4">
      <AudioLines className="size-5 text-accent" />
      <p className="min-w-[220px] flex-1 text-[14px]">
        У {versions} озвучек (≈{minutes} мин) нет меток слов — в приложении текст не подсвечивается
        во время чтения.
      </p>
      {isOwner && (
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Синхронизировать текст
        </Button>
      )}
      {run.error && <ErrorCard error={humanFromApi(run.error)} />}
      <Confirm
        open={open}
        onOpenChange={setOpen}
        title={`Синхронизировать ${String(versions)} озвучек?`}
        description={
          <>
            ElevenLabs сверит готовое аудио с текстом и расставит метки слов. Переозвучки нет. Цену
            сверки ElevenLabs не публикует — фактический расход будет в «Журнале». Можно сначала
            проверить на одной сказке (кнопка на странице сказки).
          </>
        }
        confirmLabel="Запустить"
        busy={run.isPending}
        onConfirm={() => run.mutate()}
      />
    </div>
  );
}
